import {assert} from 'Asserts';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {getStore} from 'redux-stores/Store';

import {StaticTemplateFlowchart} from '../processFlowchartConfigEditorConstants';
import {ValidationModule} from '../useFlowchartValidationAndAutosaverControl';
import {
  flowchartConfigDirtyStateChanged,
  flowchartConfigIsSaveInFlightStateChanged,
  FlowchartConfigAction,
  flowchartConfigUpdated,
  flowchartConfigAutosaverBlockedChanged,
  flowchartConfigAutosaverPausedChanged,
} from './flowchartConfigActions';
import {
  SingleFlowchartConfigReduxData,
  FlowchartConfigMetadata,
  hasAnyAutosaverPausedConditions,
  FlowchartConfigAutosaverPausedCondition,
} from './reduceFlowchartConfig';

export interface FlowchartConfigWithMetadata {
  staticFlowchart: StaticTemplateFlowchart;
  metadata: FlowchartConfigMetadata;
}

function getAllReduxData(flowchartConfigId: string): SingleFlowchartConfigReduxData | undefined {
  return getStore().getState().models.flowchartConfigs[flowchartConfigId];
}

function isDirty(flowchartConfigId: string): boolean {
  return Boolean(getAllReduxData(flowchartConfigId)?.componentLiveProperties.isDirty);
}

function isReadOnly(flowchartConfigId: string): boolean {
  return Boolean(getAllReduxData(flowchartConfigId)?.componentLiveProperties.isReadOnly);
}

/** @package */
export function shouldSaveNow(flowchartConfigId: string): boolean {
  const modelData = getAllReduxData(flowchartConfigId);
  if (modelData == null) {
    return false;
  }

  const {componentLiveProperties, platformLiveProperties, flowchartConfigMetadata} = modelData;

  if (!componentLiveProperties.isDirty) {
    return false;
  }

  if (componentLiveProperties.isReadOnly) {
    return false;
  }

  const hasAnyPausedConditions = hasAnyAutosaverPausedConditions(
    componentLiveProperties.autosaverPausedConditions
  );
  if (hasAnyPausedConditions) {
    return false;
  }

  if (platformLiveProperties.isAutosaverBlocked) {
    return false;
  }

  assert(flowchartConfigMetadata.flowchartTypeName != null, 'Flowchart type name is not initialized.', {
    flowchartConfigId,
  });

  // Note: The pause mechanism (HAS_VALIDATION_ERRORS) should normally catch this earlier,
  // but we validate here synchronously as a safety net to guarantee blocking.
  if (flowchartConfigMetadata.rootTaskSchema != null) {
    const flowchartData = getDataToSave(flowchartConfigId);
    const plugin = PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(
      flowchartConfigMetadata.flowchartTypeName
    );

    const validationErrors = ValidationModule.validateFlowchartData(
      flowchartData.staticFlowchart,
      flowchartConfigId,
      flowchartData.metadata,
      flowchartConfigMetadata.rootTaskSchema,
      flowchartConfigMetadata.didInitialFlowchartHaveChainedRouters,
      plugin
    );

    const hasBlockingValidationErrors = validationErrors.some((error) => error.blocksPersistence);
    if (hasBlockingValidationErrors) {
      return false;
    }
  }

  return true;
}

/** @package */
export function getDataToSave(flowchartConfigId: string): FlowchartConfigWithMetadata {
  const data = getAllReduxData(flowchartConfigId);
  assert(data != null, 'Autosaver is not initialized.', {flowchartConfigId});
  assert(data.flowchartConfigMetadata != null, 'Flowchart config metadata is not initialized.', {
    flowchartConfigId,
  });
  return {
    staticFlowchart: data.staticFlowchart,
    metadata: data.flowchartConfigMetadata,
  };
}

function emitAction(action: FlowchartConfigAction): void {
  getStore().dispatch(action);
}

export function setIsFlowchartConfigAutosaverBlocked(flowchartConfigId: string, isBlocked: boolean): void {
  emitAction(flowchartConfigAutosaverBlockedChanged(flowchartConfigId, isBlocked));
}

export function setAutosaverPausedForDisplayableSaveError(
  flowchartConfigId: string,
  isPaused: boolean
): void {
  emitAction(
    flowchartConfigAutosaverPausedChanged(
      flowchartConfigId,
      FlowchartConfigAutosaverPausedCondition.HAS_DISPLAYABLE_SAVE_ERROR,
      isPaused
    )
  );
}

function sendFlowchartDataToRedux(
  flowchartConfigId: string,
  flowchartWithMetadata: FlowchartConfigWithMetadata
): void {
  emitAction(flowchartConfigUpdated({flowchartConfigId, ...flowchartWithMetadata}));
}

function setIsSaving(flowchartConfigId: string, isSaveInFlight: boolean): void {
  emitAction(flowchartConfigIsSaveInFlightStateChanged(flowchartConfigId, isSaveInFlight));
}

/**
 * Used when a save fails to immediately propagate the error to Redux.
 * The reducer will automatically set isDirty=true when it sees isSaveInFlight=false + error.
 */
export function setSaveInFlightWithError(
  flowchartConfigId: string,
  isSaveInFlight: boolean,
  error: Error
): void {
  emitAction(flowchartConfigIsSaveInFlightStateChanged(flowchartConfigId, isSaveInFlight, error));
}

// Not a callback for AutosaveManager, but useful separately
export function setIsDirty(flowchartConfigId: string, isDirty: boolean): void {
  emitAction(flowchartConfigDirtyStateChanged(flowchartConfigId, isDirty));
}

// Ensure that an object is added to the store
/** @package */
export function seed(flowchartConfigId: string): void {
  setIsDirty(flowchartConfigId, false);
}

export default {
  seed,
  isDirty,
  isReadOnly,
  shouldSaveNow,
  getDataToSave,
  setIsSaving,
  sendFlowchartDataToRedux,
};
